/**
 * Typed client for the routes of `oop_contracts §6` and `plan §5.1` (P9.3).
 *
 * **The types below are transcribed from §6, not inferred from a live response.** That is the
 * point of the file: it is the TypeScript half of a cross-language contract whose Python half is
 * `server.py`'s dataclasses, and nothing type-checks both ends. A type read off a response that
 * happened to be correct on the day would agree with the server and not with the contract, which
 * is the failure this exists to make visible.
 *
 * **Same origin, and the session cookie rides along.** `V8` puts the bundle and the API behind one
 * host, so every request here is a relative path with `credentials: 'same-origin'`. That is also
 * how tile fetches authenticate: deepscatter's `tileProxy` — the documented hook for wrapping them
 * — is `throw new Error('Not yet supported')` in `3.0.0-next.51` (`plan §11` open point 8), so
 * there is nothing to intercept with and nothing that needs intercepting.
 *
 * **Every 4xx body has one shape: `{"detail": "<message>"}`** (`V56`). The server flattens
 * FastAPI's nested 422 into it, so `ApiError.detail` is always the message meant for a human and
 * never a list of pydantic error objects.
 */

/** `oop_contracts §1.1`. One projection's entry in the manifest. */
export interface ProjectionSpec {
  name: string;
  kind: 'pca' | 'umap' | 'raw';
  n_covered: number;
  n_fitted: number;
  params: Record<string, unknown>;
  tiled: boolean;
}

/** `oop_contracts §1.1`. `role` is what decides how the UI may render a column (`V18`). */
export interface ColumnSpec {
  name: string;
  arrow_type: string;
  role: 'index' | 'categorical' | 'continuous' | 'boolean';
}

/** `oop_contracts §1.1`, as `AtlasManifest.to_dict()` writes it: lists throughout, no tuples. */
export interface AtlasManifest {
  atlas_id: string;
  checkpoint_path: string;
  checkpoint_sha256: string;
  corpus_path: string;
  corpus_sha256: string;
  seed: number;
  fractions: [number, number, number];
  latent_dim: number;
  /** Rows in the atlas: `latents.f16`, `sequences.txt` and `columns.arrow` all have this many. */
  n_rows: number;
  /**
   * Rows in the **corpus**, which exceeds `n_rows` when the atlas is a subsample of it (`V83`).
   *
   * **This is the denominator `V11`'s disclosure needs, and `n_rows` stopped being it at Phase
   * 15.** A 100,000-row atlas drawn from 10,000,000 has projections covering all 100,000 of its
   * own rows, so measured against `n_rows` every one of them reports a complete view. Read this
   * instead wherever the question is "what is missing from the screen".
   */
  n_corpus_rows: number;
  vocab_size: number;
  max_length: number;
  projections: ProjectionSpec[];
  columns: ColumnSpec[];
  built_at: string;
  peptide_vis_version: string;
  quadfeather_version: string;
  /**
   * What one row of this atlas **is** (`V95`, P16.1, declared here at P29.1).
   *
   * `'peptide'` for the maps this app was built for; `'residue'` for the four alphabet atlases,
   * whose rows are monomers and whose coordinates are chemistry or a column of the embedding's
   * weight matrix. `server.py` refuses `/decode` and `/encode` on those with a 409, because a
   * decoder run over one returns a sequence that means nothing and says nothing about it.
   *
   * **In every `manifest.json` since Phase 16 and read by nothing until now.** The frontend
   * therefore had no way to tell the two apart and offered the decoder tool on both, relying on
   * the server's message to explain the refusal. That is tolerable for one option in a dropdown
   * and not for a whole composer panel, which would invite someone to type a peptide that can
   * never be placed — so `main.ts` hides `#encoder` when this is not `'peptide'`.
   *
   * Declared optional because `AtlasManifest.from_dict` defaults it (`atlas.py:302`) exactly as it
   * defaults `n_corpus_rows`, so a manifest written before Phase 16 simply lacks the key on disk
   * and `vite.config.ts`'s dev middleware, which `JSON.parse`s the file, would hand back
   * `undefined`. `isPeptideAtlas` below is the one place that is resolved.
   */
  row_kind?: string;
}

/**
 * Whether this atlas's rows are peptides, and therefore whether it can encode or decode at all.
 *
 * **Absent reads as `'peptide'`, matching `atlas.py`'s own `setdefault`** — every atlas built
 * before `row_kind` existed was a peptide atlas, so that is the honest default rather than a
 * lenient one. One function so that three call sites cannot invent three spellings of the check.
 */
export const isPeptideAtlas = (manifest: AtlasManifest): boolean =>
  (manifest.row_kind ?? 'peptide') === 'peptide';

export interface AtlasListing {
  atlases: AtlasManifest[];
  resident: string[];
  capacity: number;
}

export interface LoadResponse {
  atlas: AtlasManifest;
  resident: string[];
  capacity: number;
  /**
   * Atlas ids this load evicted under `V17`'s two-slot rule (P12.2).
   *
   * **The server names them, rather than the client diffing `resident` against its own previous
   * answer.** That diff is against a snapshot another tab may already have invalidated, so the
   * notice would name the wrong atlas — and a wrong eviction notice is worse than none.
   */
  evicted: string[];
}

/** `oop_contracts §3.4`'s three fills. `hybrid` is the **decoder tool's** default, not the app's. */
export type Fill = 'nearest' | 'inverse' | 'hybrid';

/** `oop_contracts §3.5`. A separate axis from the fill, and every response names both. */
export type Sampler = 'greedy' | 'temperature' | 'top_k' | 'top_p';

/**
 * `oop_contracts §6`. **Exactly one of `xy` / `row` / `z`** — two is a 4xx, not a precedence rule.
 *
 * **`row`, never `ix`** (`V51`). A tile datum carries both; `ix` is deepscatter's rendering rank
 * and on a subsampled view it names a different peptide. `PointDatum.row` is what goes here.
 */
export interface DecodeRequest {
  fill?: Fill;
  projection?: string;
  xy?: [number, number];
  row?: number;
  z?: number[];
  sampler?: Sampler;
  sampler_params?: Record<string, number>;
}

export interface DecodeResponse {
  sequence: string;
  /** `nearest` | `inverse` | `hybrid`, or `row` / `z` when no fill ran. */
  fill: string;
  sampler: string;
  exact: boolean;
  neighbour_row: number | null;
  z_norm: number;
}

export interface EncodeRequest {
  sequence: string;
  projection: string;
  /**
   * Whether the server should also decode the `z` it just computed (`V140`, P29.1).
   *
   * **Encoding and reconstructing are two model runs and only one of them places a point.**
   * `model.encode` is one forward pass; the reconstruction under it is an autoregressive decode
   * that measured **269.1 ms of a 316.8 ms round trip on `ancestor_10m_kl003` — 85%**. Nothing in
   * this app shows a round trip, so every call site here passes `false` and a 200-row import costs
   * ~9.5 s instead of ~67 s.
   *
   * **Optional, and the server defaults it to `true`.** That default is what made the field
   * additive: the route's only callers when it was added were eight Python tests, and they still
   * see the response they were written against.
   */
  reconstruct?: boolean;
}

export interface EncodeResponse {
  z: number[];
  xy: [number, number];
  /**
   * `null` when the request passed `reconstruct: false` — **not asked**, which is a different
   * fact from asked-and-differed (`V140`). Always `null` in this app; see `EncodeRequest`.
   */
  reconstruction: string | null;
  /**
   * Computed server-side, so there is one definition of equality rather than one per frontend.
   * `null` exactly when `reconstruction` is — the two travel together or not at all.
   */
  reconstruction_matches_input: boolean | null;
  nearest_row: number;
  nearest_distance: number;
}

/**
 * `oop_contracts §6`. A latent this server can resolve **without reference to another latent**.
 *
 * Exactly one of `row` / `sequence` / `xy`, and `fill` is required with `xy` and refused without
 * it: a coordinate does not name a latent until something says how the missing `D-2` dimensions
 * are chosen (`V15`).
 */
export interface LeafRef {
  row?: number;
  sequence?: string;
  xy?: [number, number];
  fill?: Fill;
}

/**
 * `oop_contracts §6`. One end of an interpolation: a leaf, or **an affine combination of leaves**
 * (`V144`).
 *
 * The fourth kind is what an interpolated point carries. Its latent was computed rather than read,
 * encoded or filled, so it is the one point on this map whose input is none of the other three —
 * and rather than store its 256 floats (5,243 characters as JSON, 696 as base64 float16) the
 * record keeps the recipe, measured at **170–478 characters**.
 *
 * **Flat, never nested**: `combination` holds `LeafRef`s by type. Linear interpolation is closed
 * under affine combination, so `interpolation.ts` flattens a chain before it is sent and the
 * server resolves a loop rather than a recursion.
 *
 * `checkpoint_sha` is the claim "this reference was built under that model". Sent because a recipe
 * is a *promise to recompute*, and recomputing through a different model gives a different vector
 * than the one that produced the sequence on screen; the server answers 409 when it does not
 * match (§3.4 proviso 1).
 */
export interface LatentRef extends LeafRef {
  combination?: [LeafRef, number][];
  checkpoint_sha?: string;
}

/**
 * `oop_contracts §6`. `steps` counts the **interior** points; the walk that comes back is
 * `steps + 2` long, because both endpoints are decoded too.
 */
export interface InterpolateRequest {
  start: LatentRef;
  end: LatentRef;
  projection: string;
  steps?: number;
  sampler?: Sampler;
  sampler_params?: Record<string, number>;
}

/**
 * `oop_contracts §6`. One point of the walk.
 *
 * **`endpoint` and `matches_endpoint` are two different facts** and the app must not collapse
 * them. The first says *this step is an endpoint*. The second says *this interior step decoded to
 * the same sequence an endpoint did* — a peptide at a coordinate the endpoint does not sit at,
 * which is not a new peptide. Measured over five random row pairs, the first interior step matched
 * `t = 0` in five cases out of five, so a UI that ignored the second would present two
 * reconstructions per run as discoveries.
 *
 * `duplicate_of` names the **first interior** step bearing this sequence, by `t`. `null` means this
 * step is that first bearer, which is the one the map draws.
 *
 * **The endpoints sit outside that ordering**, and that is what makes the two flags independent.
 * Ranked over the whole walk it is not symmetric: a step matching the start has its twin at a
 * lower index and is suppressed, a step matching the end has its twin at a higher index and
 * survives — so whether an endpoint's reconstruction counted as a discovery would depend on which
 * end of the line it sat near.
 */
export interface InterpolationStep {
  index: number;
  t: number;
  endpoint: 'start' | 'end' | null;
  sequence: string;
  xy: [number, number];
  z_norm: number;
  duplicate_of: number | null;
  matches_endpoint: 'start' | 'end' | null;
}

/**
 * `oop_contracts §6`. **Every step, flagged — never only the survivors** (`V145`).
 *
 * "10 latents, 7 unique sequences" is only sayable if both numbers arrive, and a response carrying
 * seven rows would leave the other three unaccounted for. `n_unique` is computed server-side so
 * that the number on screen and the number of points placed cannot disagree.
 */
export interface InterpolateResponse {
  steps: InterpolationStep[];
  /** `"linear"` today. Names the rule, so a second one would be a new value rather than a new meaning. */
  path: string;
  sampler: string;
  latent_dim: number;
  n_unique: number;
}

/**
 * The step-count ceiling. **Mirrors `interpolate.MAX_STEPS`**, as `SAMPLE_HEADER` mirrors its
 * server constant.
 *
 * It is a usefulness bound rather than a cost bound, and the difference is what the panel says:
 * measured on a 256-dimensional latent, a pair yielding 13 distinct peptides yields the same 13 at
 * 50 steps and at 100, while the batched decode costs 176.6 ms at 50 against 133.8 ms at 10.
 */
export const MAX_STEPS = 50;

/** `interpolate.DEFAULT_STEPS`, and the reader's own number. */
export const DEFAULT_STEPS = 10;

/** `interpolate.MAX_TERMS` — a **time** bound on a recipe, not a storage one (`V144`). */
export const MAX_TERMS = 16;

/** Past this the unique count stops moving; the panel says so rather than implying more is more. */
export const SATURATION_STEPS = 20;

export const interpolate = (
  atlasId: string,
  body: InterpolateRequest,
): Promise<InterpolateResponse> =>
  post<InterpolateResponse>(`/atlases/${encodeURIComponent(atlasId)}/interpolate`, body);

/** `oop_contracts §6`. The full record for one row, including the latent the tiles omit (`V13`). */
export interface PointRecord {
  row: number;
  sequence: string;
  z: number[];
  columns: Record<string, unknown>;
}

/** A 4xx or 5xx, carrying the server's message rather than a status code alone (`§0.5`). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

/** Thrown when the session cookie is missing or expired. Separate so the app can show the login. */
export class Unauthenticated extends ApiError {
  constructor(detail: string) {
    super(401, detail);
    this.name = 'Unauthenticated';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers =
    init.body === undefined
      ? init.headers
      : { 'content-type': 'application/json', ...init.headers };
  const response = await fetch(path, { ...init, credentials: 'same-origin', headers });
  if (response.ok) {
    return (await response.json()) as T;
  }
  let detail = `${response.status} ${response.statusText}`;
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === 'string') detail = body.detail;
  } catch {
    // A non-JSON error body — a proxy's 502 page, say. The status line is the honest message.
  }
  throw response.status === 401
    ? new Unauthenticated(detail)
    : new ApiError(response.status, detail);
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const login = (password: string): Promise<{ authenticated: boolean }> =>
  post('/login', { password });

/**
 * Backfill `n_corpus_rows` for an atlas built before Phase 15 declared it (`V120`).
 *
 * **`atlas.py` already does exactly this and the frontend assumed it always would.**
 * `AtlasManifest.from_dict` is `data.setdefault("n_corpus_rows", data.get("n_rows", 0))`
 * (`atlas.py:301`), which is right: before `V83` an atlas *was* its corpus, so `n_rows` is the
 * honest denominator for one built then. Every manifest that reaches the app through
 * `scripts/serve.py` has therefore been through that default, and `AtlasManifest.n_corpus_rows`
 * is declared `number` here on the strength of it.
 *
 * **There is a second reader of the same file that does not apply it**: `vite.config.ts`'s
 * `serveAtlases` middleware, which `JSON.parse`s `manifest.json` off disk so that
 * `app.browser.test.ts` can run with no Python at all. Against `thanatin_seeded` (built
 * 2026-08-19) the field is simply absent, `renderStatus` reaches
 * `manifest.n_corpus_rows.toLocaleString()`, and the whole app dies with
 * `TypeError: Cannot read properties of undefined` — the map drawn, the status line replaced by
 * the exception, and every browser test timing out on a wait that names the wait rather than the
 * cause.
 *
 * Fixed here rather than in the middleware because here covers both readers, and because a
 * declared-but-absent field is a property of the *manifest format's history*, not of the dev
 * server. Fixed at the parse boundary rather than with a `??` at each use so the type stays
 * honest: three sites read it and a fourth would forget.
 */
function withCorpusRows<T extends { n_rows: number; n_corpus_rows?: number }>(manifest: T): T {
  if (manifest.n_corpus_rows === undefined || manifest.n_corpus_rows === null) {
    manifest.n_corpus_rows = manifest.n_rows;
  }
  return manifest;
}

export const listAtlases = async (): Promise<AtlasListing> => {
  const listing = await request<AtlasListing>('/atlases');
  listing.atlases.forEach(withCorpusRows);
  return listing;
};

export const loadAtlas = async (atlasId: string): Promise<LoadResponse> => {
  const loaded = await post<LoadResponse>(`/atlases/${encodeURIComponent(atlasId)}/load`);
  withCorpusRows(loaded.atlas);
  return loaded;
};

export const decode = (atlasId: string, body: DecodeRequest): Promise<DecodeResponse> =>
  post<DecodeResponse>(`/atlases/${encodeURIComponent(atlasId)}/decode`, body);

export const encode = (atlasId: string, body: EncodeRequest): Promise<EncodeResponse> =>
  post<EncodeResponse>(`/atlases/${encodeURIComponent(atlasId)}/encode`, body);

/**
 * Coordinates for a whole list, in one forward pass (`V151`, P31).
 *
 * **The curated overlays' route, not a faster `/encode`.** It answers `xy` and nothing else — no
 * `z`, no nearest row, no reconstruction — because its caller is drawing dots and caches two
 * floats per sequence. Anything more is one `/encode` away for the single point a reader clicks.
 *
 * **All or nothing:** one out-of-alphabet code refuses the whole batch, with the offending row's
 * index in the message. Half a set is a set nobody can characterise.
 */
export interface EncodeBatchRequest {
  sequences: string[];
  projection: string;
}

export interface EncodeBatchResponse {
  /** `xy[i]` belongs to `sequences[i]`. Same length, same order. */
  xy: [number, number][];
}

export const encodeBatch = (
  atlasId: string,
  body: EncodeBatchRequest,
): Promise<EncodeBatchResponse> =>
  post<EncodeBatchResponse>(`/atlases/${encodeURIComponent(atlasId)}/encode_batch`, body);

export const point = (atlasId: string, row: number): Promise<PointRecord> =>
  request<PointRecord>(`/atlases/${encodeURIComponent(atlasId)}/point/${row}`);

export const latentStats = (atlasId: string): Promise<Record<string, unknown>> =>
  request<Record<string, unknown>>(`/atlases/${encodeURIComponent(atlasId)}/latent_stats`);

/** One residue's identity and its precomputed 2D depiction (`P18.2`). */
export interface ResidueCardData {
  code: string;
  label: string;
  smiles: string;
  inchikey: string;
  backbone_class: string;
  code_source: string;
  /** Inline `<svg>`, or `''` when the monomer's SMILES did not parse. */
  svg: string;
}

/**
 * **Not under `/atlases/{id}/`, because a residue is not a property of an atlas.** `X0067` is the
 * same monomer on the peptide map, on the chemistry map and on the embedding map; the bundle
 * belongs to the library revision and the route says so by its shape.
 */
export const residue = (code: string): Promise<ResidueCardData> =>
  request<ResidueCardData>(`/residues/${encodeURIComponent(code)}`);

/** What `GET /residues` answers: the library's own sha256, and every code it can draw. */
export interface ResidueCodes {
  revision: string;
  codes: string[];
}

/**
 * Every residue code the depiction library carries — **the composer's completion list** (P29.1).
 *
 * ## This is not the same set as "codes this model accepts", and that is accepted rather than fixed
 *
 * `/encode` validates against the **checkpoint's** `tokenizer.vocab`; this route serves the
 * **alphabet revision's** drawable codes. Two build artefacts, two commands, two inputs, and
 * nothing makes them agree. Measured 2026-08-29: they are **identical on
 * `ancestor_10m_kl003`** (1,203 each, zero difference in both directions) because the depictions
 * were built from the very `alphabet.json` that checkpoint was trained on — its `revision` is that
 * file's sha256. On the deprecated `thanatin_seeded` they differ by six, `HYP` and `NLE` among
 * them.
 *
 * They agree by build history, and no code can check it: a peptide atlas's manifest records
 * `corpus_sha256` for its `peptides.csv` and **never names its alphabet**, so there is nothing on
 * this side to compare `revision` against.
 *
 * **What makes that safe is the composer, not this function.** The list is a convenience and never
 * a ceiling: `panels/encoder.ts` lets an unlisted code be typed and submitted anyway, and the
 * server answers either a coordinate or a 400 naming the code (`server.py:436`). So the failure
 * mode this mismatch could cause — a residue the model knows going unofferable — costs a
 * completion, not the ability to place the peptide. The label on the control says
 * `codes with a structure in the library` rather than claiming to be the model's alphabet.
 *
 * **A 409 is not an error here.** An installation that never ran
 * `scripts/build_residue_depictions.py` has no bundle, and the maps are documented to work without
 * one — so the caller degrades to free text rather than failing.
 */
export const residueCodes = (): Promise<ResidueCodes> => request<ResidueCodes>('/residues');

/**
 * The curated peptide lists this installation carries (`V150`, P31).
 *
 * **This is the specific corner of the app.** Everywhere else, what the UI offers comes from a
 * manifest and is dispatched on a declared role (`V18`). These three sets are named in code, on
 * both sides, because three cases do not pay for a declaration format — see `curated.ts`'s header
 * and `PLAN_CURATED_SETS.md §13`, which records what generalising would mean and names the
 * trigger: a fourth set.
 *
 * **Not per atlas.** The same 200 candidates are the same 200 whichever atlas is loaded; only
 * their *coordinates* are per atlas and per projection, and those come from `encodeBatch`.
 *
 * **An installation without the lists answers `{revision: '', sets: []}` with a 200**, where
 * `residueCodes` answers 409. The difference is not an inconsistency: a 4xx makes the browser log
 * a console error whatever this client does with the promise, and these lists are fetched on every
 * page load where the residue codes are fetched only when a composer opens. The caller renders no
 * controls for an empty list, which is the same thing it would do with a caught 409 and one fewer
 * red line in everybody's console.
 */
export interface CuratedRow {
  sequence: string;
  /** May be empty. Never a duplicate-suppressed row — the sets are served as written. */
  label: string;
}

export interface CuratedSet {
  id: string;
  caption: string;
  rows: CuratedRow[];
}

export interface CuratedLists {
  /** sha256 over the served files, so the health panel can say which copy this is. */
  revision: string;
  sets: CuratedSet[];
}

export const curatedLists = (): Promise<CuratedLists> => request<CuratedLists>('/curated');

/**
 * A raw-dimension view's projection name: `raw:<i>,<j>` (`V65`, P11.3).
 *
 * **The name *is* the pair, because there is no manifest entry to name it by.** `C(D, 2)` pairs —
 * 496 at `D = 32`, 32,640 at `D = 256` — makes one entry per pair impossible (`V12`), so the
 * string carries the two dimensions itself. Written in one place on each side of the wire: this
 * function here, `peptide_vis.projections.raw_projection_name` there.
 *
 * It is also what goes in the URL, which is what makes a pair shareable (P11.3's DoD).
 */
export const RAW_PREFIX = 'raw:';

export const rawProjectionName = (i: number, j: number): string => `${RAW_PREFIX}${i},${j}`;

/** `"raw:3,7" -> [3, 7]`; `null` for an ordinary projection name or a malformed raw one. */
export function parseRawProjection(name: string): [number, number] | null {
  if (!name.startsWith(RAW_PREFIX)) return null;
  const parts = name.slice(RAW_PREFIX.length).split(',');
  if (parts.length !== 2) return null;
  const [i, j] = parts.map((part) => Number.parseInt(part, 10));
  return Number.isInteger(i) && Number.isInteger(j) ? [i, j] : null;
}

/** What `/rawdims` answered: the Arrow IPC bytes, and how many rows the server actually chose. */
export interface RawDimsResponse {
  buffer: Uint8Array;
  /**
   * **The server's count, not `n`** (P11.2). `n` requests *at most* `n` rows and the answer is
   * `min(n, n_rows)`; `V11`'s disclosure is a claim about what is on screen, so the figure the UI
   * prints has to be the one the server chose. It rides in a header because the body is an Arrow
   * IPC stream deepscatter parses — wrapping it to carry one integer would mean parsing twice.
   */
  sample: number;
}

/** Where `/rawdims` states its sample size. Mirrors `server.RAWDIMS_SAMPLE_HEADER`. */
export const SAMPLE_HEADER = 'x-peptide-vis-sample';

/**
 * `GET /atlases/{id}/rawdims?i=&j=&n=` — an untiled Arrow buffer for one dimension pair (`V12`).
 *
 * **Bytes, not JSON**, so this is the one route that does not go through `request`. Its errors are
 * still `{"detail": ...}` (`V56`), so the failure path reads the same body every other route
 * produces.
 */
export async function rawDims(
  atlasId: string,
  i: number,
  j: number,
  n?: number,
): Promise<RawDimsResponse> {
  const query = new URLSearchParams({ i: String(i), j: String(j) });
  if (n !== undefined) query.set('n', String(n));
  const response = await fetch(
    `/atlases/${encodeURIComponent(atlasId)}/rawdims?${query.toString()}`,
    { credentials: 'same-origin' },
  );
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // A non-JSON error body. The status line is the honest message.
    }
    throw response.status === 401 ? new Unauthenticated(detail) : new ApiError(response.status, detail);
  }
  const buffer = new Uint8Array(await response.arrayBuffer());
  const stated = Number(response.headers.get(SAMPLE_HEADER));
  // A proxy that drops the header is a real possibility and a silent zero would read as "this
  // view is empty". Falling back to `n` is the client's assumption, stated as such rather than
  // dressed up as the server's answer.
  return { buffer, sample: Number.isFinite(stated) && stated > 0 ? stated : (n ?? 0) };
}

/**
 * The tile directory a `Scatterplot` is pointed at. **A URL, not a fetch** — deepscatter does the
 * fetching, and it inherits the session cookie because this is the same origin (`V8`).
 */
export const tilesUrl = (atlasId: string, projection: string): string =>
  `/atlases/${encodeURIComponent(atlasId)}/tiles/${encodeURIComponent(projection)}`;

/**
 * `oop_contracts §3.4`, as data. **The UI greys out what this says is invalid and the server
 * refuses it anyway** (§0.5): the UI's job is to grey the option out, the server's job is to
 * assume the UI has a bug.
 *
 * Derived from the projection's `kind` rather than requested, because it is a property of the
 * projection *type* — a round trip to learn it would be a round trip to learn a constant.
 */
export function validFills(kind: ProjectionSpec['kind']): Record<Fill, boolean> {
  // UMAP has no exact basis: its axes are not latent dimensions, so there is nothing for hybrid
  // fill to hold fixed. `inverse` is available there only because `V47` reconstructs one.
  const exactBasis = kind === 'pca' || kind === 'raw';
  return { nearest: true, inverse: true, hybrid: exactBasis };
}
